// Plain-language UI definitions derived from OPPORTUNITY_DETECTION_GUIDE.md and
// SYSTEM_WORKFLOW_AND_DATA.md. Keep these summaries aligned with the scoring,
// metric-grain, and suppression contracts when any of them changes.
const DEFAULT_ACTION_INFO = {
  meaning:
    "This campaign path groups covered people whose actionable opportunity maps to this recommended action.",
  detection:
    "Eligible covered people are evaluated using qualifying claims, timing, utilization, provider, authorization, and program-specific signals.",
  opportunity: "Use this path when the current claims-based signal supports the selected benefit action.",
  caution:
    "An opportunity is not a diagnosis or an automatic clinical decision. Guardrail-suppressed covered people are excluded from routine outreach.",
};

export const ACTION_DETECTION = {
  msk: {
    "Early MSK education and self-management": {
      detection:
        "A qualifying hip, knee, shoulder, neck, or low-back diagnosis starts a new acute episode. A gap of more than 180 days starts a separate same-region episode.",
      opportunity:
        "Offer early education, self-management, or digital triage before the episode becomes persistent.",
      caution:
        "This is a claims-based episode signal; it does not establish current severity or clinical appropriateness.",
    },
    "Digital MSK / PT outreach": {
      detection:
        "The same-region episode has at least two service dates without physical therapy or other conservative care, or it persists for at least 84 days or three calendar months.",
      opportunity:
        "Connect the covered person with digital MSK support or physical therapy; persistent episodes receive higher priority.",
      caution:
        "Recent trauma, fracture, cancer or infection, progressive neurologic deficit, or surgery routes the record to review instead.",
    },
    "Digital MSK plus care navigation": {
      detection:
        "The episode shows escalation such as advanced imaging, an injection or pain procedure, emergency or urgent care, or pain-management specialty involvement.",
      opportunity:
        "Pair digital MSK support with navigation because the covered person appears to be moving to higher-intensity care.",
      caution:
        "Escalation is inferred from claims and should not be interpreted as confirmation that a specific treatment is needed.",
    },
    "Surgery second opinion / prehab review": {
      detection:
        "An orthopedic or neurosurgical consult and advanced imaging appear with a surgery-consistent diagnosis, plus sufficient episode duration, failed conservative care, or authorization evidence.",
      opportunity:
        "Offer second-opinion, shared-decision, or prehabilitation support before a possible procedure.",
      caution:
        "The distinct scheduled/authorized stage is not currently produced. This action is based on the implemented pre-surgical pathway signals.",
    },
    "Post-op recovery / rehab support review": {
      detection:
        "A surgery claim is attached to the same-region MSK episode; rehabilitation is recognized only after an attached surgery.",
      opportunity:
        "Offer recovery, rehabilitation, and post-operative navigation support to the covered person.",
      caution:
        "The claims signal confirms billed services, not the covered person's recovery status or readiness to engage.",
    },
  },
  diabetes: {
    "Diabetes Prevention Program outreach": {
      detection:
        "Prediabetes, abnormal glucose, obesity or metabolic risk, or relevant history is present without stronger active-diabetes evidence.",
      opportunity: "Offer a Diabetes Prevention Program or lower-intensity prevention support.",
      caution:
        "Prediabetes alone does not place the covered person in the active diabetes care-gap population.",
    },
    "Monitoring, education, routine diabetes support": {
      detection:
        "A diabetes diagnosis or diabetes-device supply claim is present, with no recent poor-control, acute escalation, or complex-complication signal.",
      opportunity: "Offer monitoring, education, and routine diabetes support.",
      caution: "Claims establish an active registry signal but do not describe current laboratory control.",
    },
    "Routine diabetes care-gap closure": {
      detection:
        "An active diabetes signal is present, but no qualifying claims evidence was found in the last 365 days for A1c/GMI testing, a retinal exam, or kidney evaluation.",
      opportunity: "Prompt routine care-gap closure for the missing evidence category.",
      caution:
        "This is an evidence gap, not proof that care was missed; services outside the available claims or incomplete lab data may not appear.",
    },
    "Digital diabetes management outreach": {
      detection: "Hyperglycemia or another implemented poor-control signal appears within the last 365 days.",
      opportunity: "Offer digital diabetes management for more active monitoring and support.",
      caution: "The claims-based signal does not replace clinical assessment or current laboratory results.",
    },
    "Higher-touch diabetes care management": {
      detection:
        "Evidence includes DKA/HHS, hypoglycemia, diabetes-related emergency or inpatient use, CKD/nephropathy, retinopathy, or neuropathy. Acute utilization and glycemic events use the last 365 days.",
      opportunity:
        "Use a higher-touch diabetes care-management path because acute use or complications indicate escalation.",
      caution:
        "Guardrails such as ESRD/dialysis, hospice, cancer/transplant, or pregnancy require specialized routing or review.",
    },
    "Complex diabetes case-management review": {
      detection: "The diabetes context includes a foot ulcer, amputation, or a complex cardiorenal signal.",
      opportunity: "Route to complex case-management review rather than routine digital outreach.",
      caution:
        "This high-acuity signal requires human review and should not trigger an automatic clinical conclusion.",
    },
  },
  telehealth: {
    "Self-service education": {
      detection:
        "This route is defined for self-service education, but the current source feed does not generate it directly.",
      opportunity: "Use low-intensity education when a supported self-service source becomes available.",
      caution: "Do not infer this route from an absent source signal.",
    },
    "Async message or e-visit opportunity": {
      detection: "A qualifying asynchronous message, e-visit, or related virtual-care code is present.",
      opportunity: "Direct the covered person to an asynchronous message or e-visit option.",
      caution: "Urgent or hands-on-care indicators take precedence over convenience routing.",
    },
    "Scheduled virtual visit opportunity": {
      detection:
        "The encounter reflects a low-acuity condition, behavioral health, stable chronic follow-up, medication management, or prior virtual-care use.",
      opportunity: "Offer a scheduled virtual visit as an appropriate access path.",
      caution: "Suitability is claims-based; symptoms and current clinical status still need confirmation.",
    },
    "Virtual-first triage outreach": {
      detection:
        "A low-acuity emergency or urgent-care signal has a diagnosis suitable for virtual triage and no urgent guardrail.",
      opportunity: "Offer virtual-first triage to help the covered person choose the next care setting.",
      caution:
        "Emergency-department signals qualify only when the diagnosis is low acuity and no safety guardrail is present.",
    },
    "Hybrid virtual monitoring / follow-up": {
      detection:
        "Remote-monitoring or digital-management evidence indicates that virtual follow-up can complement ongoing care.",
      opportunity: "Offer a hybrid path that combines virtual monitoring with follow-up care.",
      caution: "Hands-on-care or urgent indicators override this route.",
    },
    "Route to in-person or hybrid care": {
      detection: "The encounter has a hands-on-care need or a pregnancy or pediatric review signal.",
      opportunity:
        "Route to in-person or carefully reviewed hybrid care instead of routine virtual-only outreach.",
      caution: "This is a safety route and requires appropriate clinical or operational review.",
    },
    "Urgent or emergency routing review": {
      detection:
        "Signals include cardiac or respiratory concern, stroke or neurologic concern, behavioral crisis, trauma, fracture, head injury, burn, or acute abdominal concern.",
      opportunity:
        "Prioritize urgent or emergency routing review; this is not a routine campaign opportunity.",
      caution: "The highest-safety route always wins over convenience-oriented virtual-care routes.",
    },
  },
};

export const ENROLLMENT_DETECTION = {
  matched: {
    meaning:
      "Eligible workers matched to the current data. Covered people includes those workers and their dependents.",
    detection:
      "The application uses the matched Workday eligibility snapshot for the current evidence period. The overview uses the largest program-level eligible population so the same person is not added repeatedly across programs.",
    opportunity: "Use this as the eligibility denominator; it is not itself a campaign audience.",
    caution: "Unmatched and ineligible people are excluded.",
  },
  goal1: {
    meaning:
      "Eligible workers with an opportunity who are not enrolled. Covered people includes their dependents.",
    detection:
      "The covered person is eligible, not enrolled in the selected program, has a current program opportunity, and has no guardrail or suppression reason that blocks routine outreach.",
    opportunity: "Create an enrollment campaign that explains the relevant benefit and how to enroll.",
    caution: "Only records with a blank suppression reason should be used for routine outreach.",
  },
  goal2: {
    meaning:
      "Eligible workers with an opportunity who are already enrolled. Covered people includes their dependents.",
    detection:
      "The covered person is eligible, enrolled in the selected program, has a current recommended action, and has no guardrail or suppression reason that blocks routine outreach.",
    opportunity: "Create a campaign for the recommended service or care path.",
    caution:
      "The recommended action and guardrails, not the stage name alone, should drive campaign selection.",
  },
  candidates: {
    meaning:
      "Employees with an actionable opportunity for this program. Suppressed opportunities are not included.",
    detection:
      "The current run applies program scoring and precedence, then excludes stage 0, guardrail-flagged records, and records with a suppression reason.",
    opportunity:
      "They can be reviewed for an enrollment or program-use campaign according to enrollment status.",
    caution:
      "Counts are aggregate; campaign creation must continue to honor suppression and governance controls.",
  },
  suppressed: {
    meaning:
      "Employees excluded from campaigns by a guardrail. The percentage is their share of all evaluated opportunities.",
    detection:
      "A safety, clinical-review, in-person-routing, or other implemented guardrail produced a suppression reason.",
    opportunity: "Keep the record available for reporting or review, but exclude it from routine outreach.",
    caution: "Suppression is a control, not a statement that the underlying opportunity did not exist.",
  },
  largestGap: {
    meaning: "The program with the most eligible workers who have an opportunity but are not enrolled.",
  },
  enrollmentRate: {
    meaning: "Enrolled employees divided by eligible employees for this program.",
  },
  highPriority: {
    meaning: "Employees with an actionable opportunity in the High clinical score band.",
  },
};

export function getActionDetection(domain, action) {
  const details = { ...DEFAULT_ACTION_INFO, ...(ACTION_DETECTION[domain]?.[action] || {}) };
  return { ...details, meaning: details.opportunity };
}
